import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import cn from "classnames";
import styles from "../styles.modules.scss";
import { FormattedMessage } from "react-intl";
import { Sparkles } from "../../../content-card/sparkles";
import { Button } from "../../../button";
import { Image } from "../../../image";
import { useInboxRewardsContext } from "../../../../stores/inboxRewards";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { usePollContext } from "../../../../stores/poll";
import { Timer } from "../default/timer";
import { translate } from "./translations";

export type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    background?: ImageSourceSets;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting?: Targeting;
    lang: Locale;
    relatedMedia?: RelatedMedia[];
};

const DO_SPARKLE_EFFECT = window.GAMENAME === "clashofclans";

export const TopsyTurvyProgressSlide = ({
    slide,
    children,
}: {
    slide: ProgressSlideType;
    children?: React.ReactNode;
}) => {
    const { title, tracker, milestones, background, targeting, relatedMedia } =
        slide;

    const isRtl = document.querySelector(".rtl") !== null;

    const horizontalPosition = (pct: number) =>
        isRtl ? { right: `${pct}%` } : { left: `${pct}%` };

    const { actions } = useInboxRewardsContext();

    const claimedVouchers = actions.getClaimedVouchers();

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>();
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const {
        actions: { openModal },
    } = usePollContext();

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const ctas = parseCTAs(slide.ctas, handleClick);

    const fillPercentage = tracker.progress;
    const direction = tracker.direction;
    const barColor = tracker.color;

    useEffect(() => {
        const availableVouchers = milestones.filter((m) => !!m.voucherId);

        if (!availableVouchers.length) {
            return;
        }

        const voucherIds = availableVouchers.map(
            (m) => m.voucherId,
        ) as string[];

        actions.fetchUnclaimedVouchers(voucherIds);
    }, []);

    const style =
        direction === "vertical"
            ? {
                  height: `${fillPercentage}%`,
              }
            : {
                  width: `${fillPercentage}%`,
              };

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const barColorStyle = { "--bar-color": barColor } as React.CSSProperties;

    // Moco can use a related media "timer" label to show the end time of the progress tracker.
    const timerLabel = relatedMedia?.find(
        (media) => media.title === "timer",
    )?.label;

    const spotlightImage = relatedMedia?.find(
        (media) => media.title === "spotlight",
    )?.image;

    const dayLabel = relatedMedia?.find(
        (media) => media.title === "day",
    )?.label;

    const milestoneLabels = useMemo(
        () =>
            new Map(
                relatedMedia
                    ?.filter((media) => media.title?.startsWith("milestone-"))
                    .map((media) => {
                        const index =
                            media.title !== undefined
                                ? parseInt(
                                      media.title.replace("milestone-", ""),
                                      10,
                                  ) - 1
                                : -1;
                        return [index, media.label] as const;
                    })
                    .filter(([index]) => !isNaN(index)) ?? [],
            ),
        [relatedMedia],
    );

    const [endTime, setEndTime] = useState(() => {
        const endTime = new Date(timerLabel ?? 0).getTime() - Date.now();
        return endTime;
    });

    useEffect(() => {
        if (window.GAMENAME !== "rogue" || !timerLabel) return;

        const interval = setInterval(() => {
            setEndTime((prevEndTime) => prevEndTime - 1000);
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const trackersRef = useRef<HTMLDivElement>(null);
    const spotlightRef = useRef<HTMLImageElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const effectRef = useRef<HTMLDivElement>(null);

    const updateEffectPosition = useCallback(() => {
        const spotlight = spotlightRef.current;
        const content = contentRef.current;
        const effect = effectRef.current;
        if (!spotlight || !content || !effect) return;

        const spotlightRect = spotlight.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();

        const centerX =
            spotlightRect.left + spotlightRect.width / 2 - contentRect.left;
        const centerY =
            spotlightRect.top + spotlightRect.height / 2 - contentRect.top;

        effect.style.left = `${centerX}px`;
        effect.style.top = `${centerY}px`;
    }, []);

    useEffect(() => {
        updateEffectPosition();

        const observer = new ResizeObserver((entries) => {
            updateEffectPosition();

            // Shift trackers up when container is tall (e.g. iPad portrait)
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (trackersRef.current) {
                    trackersRef.current.style.transform =
                        height > width * 1.1 ? "translateY(-15%)" : "none";
                }
            }
        });

        if (contentRef.current) {
            observer.observe(contentRef.current);
        }

        return () => observer.disconnect();
    }, [updateEffectPosition, isVisible]);

    const handleTrackersScroll = useCallback(() => {
        const trackers = trackersRef.current;
        const spotlight = spotlightRef.current;
        if (!trackers || !spotlight) return;

        const maxScroll = trackers.scrollWidth - trackers.clientWidth;
        if (maxScroll <= 0) return;

        const progress = trackers.scrollLeft / maxScroll;
        const scale = 1 - progress * 0.3;
        const rotation = progress * -10;
        spotlight.style.transform = `scale(${scale}) rotate(${rotation}deg)`;

        updateEffectPosition();
    }, [updateEffectPosition]);

    useEffect(() => {
        const trackers = trackersRef.current;
        if (!trackers) return;

        trackers.addEventListener("scroll", handleTrackersScroll);

        // Prevent touch events from propagating to Swiper,
        // which causes snap-back on small swipes at scroll boundaries.
        const stopPropagation = (e: TouchEvent) => e.stopPropagation();
        trackers.addEventListener("touchstart", stopPropagation);
        trackers.addEventListener("touchmove", stopPropagation);

        return () => {
            trackers.removeEventListener("scroll", handleTrackersScroll);
            trackers.removeEventListener("touchstart", stopPropagation);
            trackers.removeEventListener("touchmove", stopPropagation);
        };
    }, [handleTrackersScroll]);

    useEffect(() => {
        const trackers = trackersRef.current;
        if (!trackers) return;

        const lastUnlocked = [...milestones].reverse().find((m) => m.unlocked);
        if (!lastUnlocked) return;

        const progress = Math.max(lastUnlocked.progress, 5) / 100;
        const effectiveProgress = isRtl ? 1 - progress : progress;
        const targetScrollLeft =
            trackers.scrollWidth * effectiveProgress - trackers.clientWidth / 2;
        trackers.scrollLeft = Math.max(0, targetScrollLeft);
    }, [milestones, isRtl, trackersRef]);

    return (
        <div
            className={`slide progress-indicator ${styles.slide} ${
                isVisible ? "visible" : ""
            } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                targeting?.campaignId ?? ""
            } ${title ? "with-title" : "without-title"}`}
            ref={ref}
            style={barColorStyle}
        >
            <div
                ref={contentRef}
                className={`content ${styles.content}`}
                style={
                    background
                        ? ({
                              "--bg-image": `url(${background.large.path})`,
                          } as React.CSSProperties)
                        : undefined
                }
            >
                {children}
                <div ref={effectRef} className="shine-effect" />
                {window.GAMENAME === "rogue" && timerLabel && (
                    <>
                        <Timer format="total-hours" countdown={endTime} />
                        <div className="timer-spacer" />
                    </>
                )}
                {title && (
                    <>
                        <div className={`filter ${styles.filter}`} />

                        <div
                            className={`title ${styles.title} `}
                            data-title={title}
                        >
                            {title}
                            {dayLabel && (
                                <span className="day-label">
                                    {translate("day", slide.lang)} {dayLabel}
                                </span>
                            )}
                        </div>
                    </>
                )}

                <div
                    className={`trackers swiper-no-swiping ${styles.trackers}`}
                    ref={trackersRef}
                >
                    <div
                        className="tracker-row"
                        style={{
                            visibility: isVisible ? "visible" : "hidden",
                        }}
                    >
                        {spotlightImage?.url && (
                            <div className="spotlight-wrapper">
                                <img
                                    ref={spotlightRef}
                                    className="spotlight-image"
                                    src={spotlightImage.url}
                                    alt={spotlightImage.title || ""}
                                    width={128}
                                    height={128}
                                />
                            </div>
                        )}
                        <div className={`tracker-wrapper`}>
                            <div className={`tracker ${styles.tracker}`}>
                                <div
                                    style={style}
                                    className={`tracker-progress ${
                                        styles["tracker-progress"]
                                    } ${
                                        fillPercentage > 99
                                            ? "almost-complete"
                                            : ""
                                    } `}
                                />
                            </div>

                            {milestones.map((milestone, index) => {
                                const milestonePosition = Math.max(
                                    milestone.progress,
                                    5, // 5% is the minimum we support without the UI looking totally wack
                                );
                                const style =
                                    direction === "vertical"
                                        ? {
                                              bottom: `${milestonePosition}%`,
                                          }
                                        : horizontalPosition(milestonePosition);

                                return (
                                    <span
                                        key={index}
                                        style={style}
                                        className="milestone-marker"
                                    />
                                );
                            })}
                        </div>
                    </div>

                    <div
                        className={`milestone-wrapper milestones-${milestones.length}`}
                    >
                        {milestones.map((milestone, index) => {
                            if (!milestone.image) {
                                return null;
                            }

                            const milestonePosition = Math.max(
                                milestone.progress,
                                5, // 5% is the minimum we support without the UI looking totally wack
                            );

                            const hasReachedMilestone = milestone.unlocked;

                            const style =
                                direction === "vertical"
                                    ? {
                                          bottom: `${milestonePosition}%`,
                                      }
                                    : isRtl
                                      ? { right: `${milestonePosition}%` }
                                      : { left: `${milestonePosition}%` };

                            const isClaimable = !!milestone.voucherId;

                            const isClaimed = claimedVouchers.includes(
                                milestone.voucherId as string,
                            );

                            return (
                                <div
                                    onClick={async () => {
                                        if (isClaimed) {
                                            return;
                                        }

                                        if (!milestone.voucherId) {
                                            return;
                                        }

                                        actions.claimQuietVoucher(
                                            milestone.voucherId,
                                        );
                                    }}
                                    style={style}
                                    key={index}
                                    className={cn(
                                        "milestone-img",
                                        styles["milestone-img"],
                                        {
                                            "milestone-achieved": isClaimable
                                                ? hasReachedMilestone &&
                                                  isClaimed
                                                : hasReachedMilestone,
                                        },

                                        {
                                            "milestone-unachieved":
                                                !hasReachedMilestone,
                                        },

                                        isClaimable
                                            ? hasReachedMilestone && isClaimed
                                            : hasReachedMilestone,
                                        {
                                            "milestone-claimable":
                                                !isClaimed && isClaimable,
                                        },

                                        !isClaimed && isClaimable
                                            ? styles["milestone-claimable"]
                                            : undefined,
                                    )}
                                >
                                    <div
                                        className={cn("milestone-frame", {
                                            "milestone-frame--unlocked":
                                                hasReachedMilestone,
                                        })}
                                    >
                                        {hasReachedMilestone && (
                                            <div className="milestone-shine" />
                                        )}
                                        {DO_SPARKLE_EFFECT ? (
                                            <Sparkles
                                                sparkleSize={{
                                                    min: 5,
                                                    max: 10,
                                                }}
                                                sparkleArea={{
                                                    top: -80,
                                                    bottom: 80,
                                                    left: -80,
                                                    right: 80,
                                                }}
                                                color="#fff"
                                                sparkleAmount={2}
                                                active={hasReachedMilestone}
                                            >
                                                <Image
                                                    sources={milestone.image}
                                                />
                                            </Sparkles>
                                        ) : (
                                            <Image sources={milestone.image} />
                                        )}
                                    </div>

                                    {milestoneLabels.get(index) && (
                                        <span className="milestone-multiplier">
                                            {milestoneLabels.get(index)}
                                        </span>
                                    )}

                                    {!hasReachedMilestone && (
                                        <div className="milestone-locked" />
                                    )}
                                    {hasReachedMilestone && (
                                        <div className="milestone-unlocked" />
                                    )}

                                    {spotlightImage?.url && (
                                        <div className="milestone-badge">
                                            <div
                                                className="milestone-badge-icon"
                                                style={{
                                                    backgroundImage: `url(${spotlightImage.url})`,
                                                }}
                                            />
                                            <span className="milestone-badge-label">
                                                {translate(
                                                    "moreChicks",
                                                    slide.lang,
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div
                        className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                    >
                        {milestones.map((milestone, index) => {
                            if (!milestone.label) {
                                return null;
                            }

                            const milestonePosition = Math.max(
                                milestone.progress,
                                5, // 5% is the minimum we support without the UI looking totally wack
                            );

                            const hasReachedMilestone = milestone.unlocked;

                            const style =
                                direction === "vertical"
                                    ? {
                                          bottom: `${milestonePosition}%`,
                                      }
                                    : isRtl
                                      ? { right: `${milestonePosition}%` }
                                      : { left: `${milestonePosition}%` };

                            return (
                                <div
                                    key={index}
                                    style={style}
                                    className={`milestone-label ${
                                        styles["milestone-label"]
                                    } ${
                                        hasReachedMilestone
                                            ? styles["milestone-reached"]
                                            : styles["milestone-unreached"]
                                    } ${
                                        hasReachedMilestone
                                            ? "milestone-reached"
                                            : "milestone-unreached"
                                    }`}
                                    title={milestone.label}
                                >
                                    {milestone.label}

                                    {hasReachedMilestone && (
                                        <div className="milestone-unlocked" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={`wrapper ${styles.wrapper}`}>
                    {ctas.length ? (
                        <div
                            className={`ctas ${
                                ctas.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {ctas.map(({ label, variant, onClick }, index) => {
                                return (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={variant}
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
